import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { inr, track } from "../lib/api";
import { useSite } from "../context/SiteContext";

const PROCESS = ["Requirement", "Estimate", "Payment", "Development", "Testing", "Deployment", "Handover"];
const FEATURES = [
  "Requirement analysis", "Stakeholder consultation", "Architecture", "UI implementation",
  "AI-assisted development", "Testing", "Agreed revisions", "Deployment",
  "Source code handover", "Credentials handover",
];

export default function Standard() {
  const { settings } = useSite();
  const rate = settings.rates.standard;

  return (
    <div className="pt-32 lg:pt-40 pb-24" data-testid="standard-page">
      <div className="container-x">
        <p className="section-label">STANDARD DEVELOPMENT</p>
        <h1 className="mt-5 font-heading text-5xl sm:text-6xl font-black tracking-tight text-white max-w-3xl">
          BUILD IT <span className="text-flame">IN DAYS.</span>
        </h1>
        <p className="mt-6 text-slate-400 max-w-2xl leading-relaxed">
          Standard Delivery is designed for businesses that want rapid development without requiring a live build
          session. Suitable applications are targeted for delivery within approximately 7 days.
        </p>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="rounded-2xl border border-white/10 bg-navy-800 p-8 sm:p-12">
            <p className="font-mono text-[11px] tracking-[0.25em] text-slate-400">FIXED HOURLY RATE</p>
            <p className="mt-4 font-heading text-6xl font-black text-white">
              {inr(rate)}<span className="text-lg font-semibold text-slate-400">/hour</span>
            </p>
            <p className="mt-5 text-xs text-slate-500 leading-relaxed">
              Many standard projects are targeted within approximately 50 development hours, subject to scope.
            </p>
            <div className="mt-5 rounded-lg bg-navy-900/80 border border-white/5 p-4 font-mono text-xs text-slate-400">
              50 hours × {inr(rate)} = <span className="text-flame font-bold">{inr(50 * rate)}</span>
              <span className="block mt-2 text-[10px] text-slate-600">
                Illustrative example only. Final pricing and timeline are confirmed after requirement analysis.
              </span>
            </div>
            <div className="mt-8">
              <p className="font-mono text-[11px] tracking-[0.25em] text-slate-400 mb-5">PROCESS</p>
              <div className="flex flex-wrap items-center gap-y-3">
                {PROCESS.map((s, i) => (
                  <div key={s} className="flex items-center">
                    <span className="rounded-full border border-white/15 px-3.5 py-1.5 text-[10px] font-semibold tracking-wider text-slate-200">{s}</span>
                    {i < PROCESS.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-flame mx-1.5 shrink-0" />}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-navy-800 p-8 sm:p-12">
            <p className="font-mono text-[11px] tracking-[0.25em] text-slate-400 mb-8">EVERY STANDARD PROJECT INCLUDES</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm text-slate-200">
                  <Check className="w-4 h-4 text-flame shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Link
              to="/get-estimate?model=standard"
              data-testid="standard-page-estimate-btn"
              onClick={() => track("estimate_click", { location: "standard_page" })}
              className="mt-12 inline-flex items-center gap-2 rounded-full bg-flame hover:bg-flame-hover text-white font-heading font-bold tracking-wide px-8 py-4 transition-colors shadow-[0_0_28px_rgba(249,115,22,0.35)]"
            >
              REQUEST STANDARD ESTIMATE <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
